// ============================================================================
// firebaseClient.js — a Firebase-szel (Auth + Firestore) beszélő EGYETLEN
// modul. A nézet- és állapot-rétegek sosem hívják közvetlenül a Firebase
// SDK-t, csak ezen a modulon keresztül - ez teszi lehetővé, hogy később,
// a Callable Functions elkészültével csak itt kelljen bővíteni.
//
// FONTOS: ez a modul jelenleg KIZÁRÓLAG a firestore.rules (v2) által
// ténylegesen engedélyezett műveleteket hívja:
//   - Google bejelentkezés (signInWithRedirect)
//   - access/{saját UID} get
// A saveProfile, sendMessage stb. Callable Functions még nem léteznek -
// ezeket a hívásokat majd itt kell hozzáadni, amikor elkészülnek.
// ============================================================================

// ----------------------------------------------------------------------
// A Firebase SDK NPM csomagból, helyben csomagolva (esbuild), NEM egy
// külső CDN-ről futásidőben betöltve - ez a SPECIFICATION.md 2.1.
// szakaszának kifejezett előírása (build kimenet: dist/, "helyben
// csomagolva"). A build folyamat (lásd package.json) ezt a "firebase"
// importot a saját bundle.js-be fordítja.
// ----------------------------------------------------------------------
import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  reauthenticateWithPopup,
  getRedirectResult,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
} from "firebase/auth";
import {
  initializeFirestore,
  getFirestore,
  memoryLocalCache,
  memoryLruGarbageCollector,
  clearIndexedDbPersistence,
  terminate,
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  startAfter,
  Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ENV } from "./env.js";
import { futasiAuthDomain } from "./authDomain.js";
import { vedettHivas } from "./authVedett.js";
import { szobaDokumentumbol } from "./roomData.js";
import { cetliDokumentumbol } from "./noteData.js";
import { kozlemenyDokumentumbol } from "./kozlemenyData.js";
import { temaDokumentumbol } from "./forumData.js";
import { javaslatBol } from "./fordJavaslat.js";
import { kivonatSzoveg } from "./kezdolap.js";
import {
  kezbesitoSzuro,
  gazdaEllenorzes,
  halozatVan,
} from "./gyorsitotar.js";
import {
  hatalyosMod,
  authMegorzes,
  tartosTorlesKell,
  torlesEredmenyIr,
  firestoreAdatbazisNev,
  sdkTarMigralasKell,
  sdkTarMigralva,
} from "./eszkozMod.js";
import { googleParameterek, kilepesJelol } from "./belepesEmlekezet.js";
import { OfflineTar, idbHatter, memoriaHatter } from "./offlineTar.js";
import { OfflineKezelo } from "./offlineKezelo.js";

// ----------------------------------------------------------------------
// A Firebase-projekt webes konfigurációja KÖRNYEZETENKÉNT más (dev / prod),
// és az env/<név>.json fájlból érkezik: a build fordítja a csomagba (lásd
// env.js). Ebben a fájlban SEMMILYEN projektazonosító nem szerepel.
//
// Az apiKey a Firebase architektúrájában nem titkos adat - csak a projektet
// azonosítja, a tényleges hozzáférés-védelmet a Firestore/Storage szabályok
// és a Google-bejelentkezés adja.
//
// A teljes konfiguráció kell (nem csak apiKey/authDomain/projectId):
//   - storageBucket: enélkül a képfeltöltés ("No default bucket found")
//     nem működik,
//   - messagingSenderId + appId: enélkül a push értesítés tokenje nem
//     kérhető le.
//
// FONTOS, VALÓS HIBÁBÓL TANULVA: az authDomain SZÁNDÉKOSAN a tényleges
// hosting-domain (<projectId>.web.app), NEM az alapértelmezett
// <projectId>.firebaseapp.com. Firebase Hosting minden saját domainjén
// automatikusan kiszolgálja a hitelesítési útvonalakat (/__/auth/**), így
// a bejelentkezési redirect UGYANAZON az origin-en marad végig - nincs
// szükség domainek közötti (harmadik féltől származó) tárolásra. Enélkül a
// redirect utáni visszatéréskor a böngésző elveszítheti a bejelentkezés
// eredményét, HIBAÜZENET NÉLKÜL. Az env-betöltő ezt az egyezést ellenőrzi.
// ----------------------------------------------------------------------
// Az authDomain FUTÁSIDEJŰ (2026-10-10): az a tartomány, amelyről az alkalmazás fut (lásd authDomain.js) - különben a saját
// tartományon (lineage0.com) a bejelentkezés eredménye elvész, és a felhasználó a Google és a belépő képernyő között pörög.
const firebaseConfig = {
  ...ENV.firebase,
  authDomain: futasiAuthDomain(ENV.firebase.authDomain, typeof window !== "undefined" ? window.location.hostname : ""),
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// --- A bejelentkezés MEGŐRZÉSE a böngésző bezárása után is
//
// A Firebase alapértelmezése is tartós, de itt KIMONDJUK, hogy ez
// szándékos, és megadjuk a tartaléksorrendet is: privát böngészésben vagy
// letiltott IndexedDB mellett az első választás elbukna, és a
// bejelentkezés csak az adott lapra érvényes maradna - a felhasználónak
// minden megnyitáskor újra be kellene lépnie.
//
// FONTOS KORLÁT, amit kóddal nem lehet megkerülni: iPhone-on a Safari
// (ITP) a szkript által írt tárolót ~7 nap tétlenség után TÖRLI, és
// ilyenkor a bejelentkezés is elvész. A kezdőképernyőre telepített
// alkalmazásra ez nem vonatkozik - ez is ok a telepítés mellett.
// KÖZÖS / KÖLCSÖNKAPOTT ESZKÖZ (2026-10-22, eszkozMod.js): ott a bejelentkezés csak a böngészőmunkamenet erejéig él.
const eszkozMod = hatalyosMod();
if (authMegorzes(eszkozMod) === "session") {
  setPersistence(auth, browserSessionPersistence).catch(() => {});
} else {
  setPersistence(auth, indexedDBLocalPersistence).catch(() =>
    setPersistence(auth, browserLocalPersistence).catch(() => {})
  );
}
// ----------------------------------------------------------------------
// FIRESTORE HELYI GYORSÍTÓTÁR (2026-10-08, költség - lásd gyorsitotar.js)
//
// Alapértelmezetten a webes SDK csak memóriában tart adatot, és a figyelő leállásakor
// elfelejti: szobaváltásnál / újranyitásnál az 50 üzenetet MINDIG újra kifizetjük.
// Tartós (IndexedDB) gyorsítótárral az újrafeliratkozás csak a változásokat számlázza
// (a Firestore dokumentációja szerint, ha a legutóbbi állapot 30 percnél frissebb).
//
//  1. tartós, több lapon is használható gyorsítótár;
//  2. ha nincs IndexedDB (privát böngészés, tiltás) vagy az indítás elbukik: memória-gyorsítótár
//     LRU takarítással (a figyelő leállása után is megőrzi az adatokat, amíg a keret engedi) -
//     ez a régi, "mindig újraolvas" viselkedésnél már jobb, és semmilyen helyi fájlt nem hoz létre;
//  3. végső tartalék: az alapértelmezett getFirestore (a régi viselkedés).
//
// A gyorsítótár NEM felhasználónkénti: közös eszközön a következő belépő is látná az előző
// tag tárolt üzeneteit. Ezért megjegyezzük, KIÉ (lásd gyorsitotarGazda): kijelentkezéskor a tár
// MEGMARAD (ugyanaz a tag tovább használja), és csak MÁS felhasználó belépésekor törlődik.
// ----------------------------------------------------------------------
// Igaz, ha a tartós (IndexedDB) gyorsítótár indítása sikerült - csak ekkor van mit törölni / felhasználóhoz kötni.
let tartosAktiv = false;
function firestoreInditasa() {
  // v143: az SDK SOHA nem használ tartós gyorsítótárat (csak memóriát). Az offline olvasást a saját, szobánként szabályozott tár adja (offlineKezelo.js);
  // az SDK tartós tára nem szűrhető szobánként, és nyílt szöveggel tárolna mindent. A régi tartós adatbázist ezért törölni kell:
  //  - közös eszközön (v134) a váltáskor, az eredményt a belépő képernyő mutatja, és sikertelen törlésig a belépés tiltott;
  //  - minden más eszközön egyszeri migrációként (sdkTarMigralasKell), csendben.
  if (tartosTorlesKell(eszkozMod) || sdkTarMigralasKell()) {
    try {
      if (typeof indexedDB === "undefined") {
        torlesEredmenyIr("ok"); // nincs tartós tár: nincs mit törölni
        sdkTarMigralva();
      } else {
        const keres = indexedDB.deleteDatabase(firestoreAdatbazisNev(firebaseConfig.projectId));
        keres.onsuccess = () => { sdkTarMigralva(); torlesEredmenyIr("ok"); };
        keres.onerror = () => torlesEredmenyIr("hiba");
        keres.onblocked = () => torlesEredmenyIr("blocked"); // egy másik fül tartja nyitva; a kérés függőben marad, és a fül bezárása után sikerül
      }
    } catch (e) {
      torlesEredmenyIr("hiba");
    }
  }
  try {
    return initializeFirestore(app, {
      localCache: memoryLocalCache({ garbageCollector: memoryLruGarbageCollector() }),
    });
  } catch (e) {
    return getFirestore(app);
  }
}
const db = firestoreInditasa();

// --- Offline pillanatkép-tár (v143, docs/OFFLINE_TERV_2026-10-22.md): saját eszköz módban az offline ENGEDÉLYEZETT szobák szerver által visszaigazolt
// tartalmát menti, kapcsolat nélkül ebből szolgál ki. Közös eszközön soha nem ment. Ha az IndexedDB nem érhető el, memória-háttértárra esik vissza.
function offlineHatterKeszit() {
  try {
    return idbHatter(typeof window !== "undefined" ? window : undefined);
  } catch (e) {
    return memoriaHatter();
  }
}
export const offline = new OfflineKezelo({ tar: new OfflineTar(offlineHatterKeszit()), halozatVan });
export function offlineAktival(uid) {
  return offline.aktival(uid, eszkozMod);
}
export function offlineLeallit() {
  return offline.leallit();
}
/** Igaz, ha a pillanatkép a SZERVERTŐL jött (nem a memória-gyorsítótárból): csak ilyet mentünk. */
const szerverrolJott = (snap) => Boolean(snap && snap.metadata && snap.metadata.fromCache === false);
/**
 * A lista forrásának jelzése a kezelőnek (v181): `megerositetlen: true`, ha a szerver még NEM igazolta vissza (a pillanatkép a helyi
 * gyorsítótárból jött, pl. akadozó hálózaton a visszatartási időkorlát után). Egy ilyen ÜRES lista nem jelent "nincs ilyen" - a nézet
 * ilyenkor nem mondhatja, hogy "még nincs téma / üzenet", és nem hívhat az első létrehozására.
 */
const forrasJelzes = (snap) => ({ megerositetlen: !(snap && snap.metadata && snap.metadata.fromCache === false) });
/** Kapcsolat nélkül az SDK memória-tára üres: az üres gyorsítótár-pillanatkép helyett a saját tár szolgál (ha van mit adni). */
const uresOfflineGyorsitotar = (snap, lista) => offline.offlineKiszolgalas() && snap && snap.metadata && snap.metadata.fromCache === true && lista.length === 0;
// Igaz, ha ebben a lapban már volt Firestore-hívás: ilyenkor egy MÁS felhasználó belépésekor a futó példányt
// le kell állítani a törlés előtt, és újratölteni (lásd gyorsitotarGazda).
let firestoreHasznalva = false;
function fs() {
  firestoreHasznalva = true;
  return db;
}
// A függvények ugyanabban a régióban futnak, ahol telepítettük őket -
// enélkül a kliens az alapértelmezett us-central1-et hívná, és 404-et kapna.
const functions = getFunctions(app, "europe-west1");

/** A Firebase app példány - a Messaging modul kéri el, igény szerint. */
export function getFirebaseApp() {
  return app;
}

// A gyorsítótár GAZDÁJA (2026-10-08): belépéskor, az első Firestore-hívás előtt ellenőrizzük, hogy a
// tárolt adat ennek a felhasználónak szól-e (lásd gyorsitotar.js). Eltérésnél vagy ismeretlen
// tulajdonosnál töröljük, és csak SIKERES törlés után jegyezzük be az új tulajdonost. A Firestore még
// nem indult el (a lekérdezések a belépés UTÁN kezdődnek), ezért a törlés engedélyezett és az
// példány utána használható marad. Ha már volt Firestore-használat (nem várt eset), nem nyúlunk
// hozzá: a kijelentkezés (signOut) amúgy is töröl és újratölt.
async function gyorsitotarGazda(uid) {
  const idokorlat = () => new Promise((_, nem) => setTimeout(() => nem(new Error("időtúllépés")), 3000));
  return gazdaEllenorzes({
    tartosAktiv,
    firestoreHasznalva,
    uid,
    torol: () => clearIndexedDbPersistence(db),
    leallitasEsTorles: async () => {
      await Promise.race([terminate(db), idokorlat()]);
      await Promise.race([clearIndexedDbPersistence(db), idokorlat()]);
    },
    ujratoltes: () => { if (typeof window !== "undefined" && window.location) window.location.reload(); },
    figyelmeztet: (e) => console.warn("[gyorsítótár] a korábbi tulajdonos adatának törlése nem sikerült:", e && e.message),
  });
}

// A hitelesítési események sorban mennek: a gazda-ellenőrzésnek a kezelő (callback) ELŐTT kell
// lefutnia, de a kezelők futása nem várakoztatja egymást (a régi viselkedés).
let hitelesitesSor = Promise.resolve();
export function watchAuthState(callback) {
  return onAuthStateChanged(auth, (user) => {
    hitelesitesSor = hitelesitesSor
      .then(() => (user ? gyorsitotarGazda(user.uid) : null))
      .catch(() => {})
      .then(() => { callback(user); });
  });
}

/**
 * Bejelentkezés Google-fiókkal, ÁTIRÁNYÍTÁSSAL.
 *
 * Ez az alapértelmezett út: telepített PWA-ban (Androidon és iPhone-on
 * egyaránt) megbízhatóan működik, ott ugyanis felugró ablakot nem lehet
 * nyitni.
 */
export function signInWithGoogle() {
  return signInWithRedirect(auth, googleSzolgaltato());
}

/**
 * A Google-szolgáltató a megjegyzett e-mail-címmel (login_hint, v182): újra belépéskor a fiókválasztó kimarad, egy koppintás elég.
 * Közös eszközön nincs megjegyzett cím. A cím nem titok, és csak az eszközön marad.
 */
function googleSzolgaltato() {
  const szolgaltato = new GoogleAuthProvider();
  const parameterek = googleParameterek(eszkozMod);
  if (parameterek.login_hint) szolgaltato.setCustomParameters(parameterek);
  return szolgaltato;
}

/**
 * Bejelentkezés Google-fiókkal, FELUGRÓ ABLAKBAN.
 *
 * Tartalék út arra az esetre, amikor az átirányítás némán elbukik. A
 * Firebase saját dokumentációja szerint ez a javasolt megoldás azokban a
 * böngészőkben, amelyek szigorúan szétválasztják a tárolást (Safari/WebKit,
 * Firefox): a felugró ablak közvetlenül beszél a megnyitó lappal, így nem
 * függ attól a tárolási csatornától, amit a böngésző elvághat.
 *
 * Telepített (standalone) módban NEM használható, mert ott a rendszer nem
 * nyit új ablakot - ezért a hívó oldalon dől el, mikor kerül sorra.
 */
export function signInWithGooglePopup() {
  return signInWithPopup(auth, googleSzolgaltato());
}

/**
 * ÚJRAHITELESÍTÉS a legérzékenyebb admin-műveletekhez (v135, a titkár #63 javaslata): a szerver az ID-token auth_time értékét
 * nézi, ezért a Google bejelentkezést ténylegesen meg kell ismételni (prompt=login), majd friss tokent kérünk. Felugró ablakkal
 * történik, így a lap és a félig kitöltött adatok megmaradnak. Ugyanazt a fiókot kell választani (különben a Firebase elutasítja).
 */
export async function ujraHitelesites() {
  const user = auth.currentUser;
  if (!user) throw Object.assign(new Error("Nincs bejelentkezett felhasználó."), { code: "app/no-user" });
  const szolgaltato = new GoogleAuthProvider();
  szolgaltato.setCustomParameters({ prompt: "login" });
  await reauthenticateWithPopup(user, szolgaltato);
  // Az újrahitelesítés már friss tokent ad; a kényszerített frissítés csak biztonsági öv. Ha a hálózat / a kulcs-korlátozás ezt
  // elutasítja (élő tapasztalat: a beágyazott böngészőpanelben a securetoken-hívás 403), az nem tehet sikertelenné egy sikeres belépést.
  try {
    await user.getIdToken(true);
  } catch (e) {
    /* a reauthenticate által kiadott token érvényes */
  }
}

export function consumeRedirectResult() {
  return getRedirectResult(auth);
}

/**
 * A bejelentkezési token KÉNYSZERÍTETT frissítése. Nem dob kivételt, hanem
 * eredményt ad: { ok: true } vagy { ok: false, code } - a hívó dönti el, mit
 * kezd a kudarccal.
 */
export async function frissitsdATokent() {
  if (!auth.currentUser) return { ok: false, code: "app/no-current-user" };
  try {
    await auth.currentUser.getIdToken(true);
    return { ok: true };
  } catch (e) {
    return { ok: false, code: String((e && e.code) || "auth/unknown") };
  }
}

// A Callable-hívások tokenvédelme (2026-10-03) külön, tesztelhető modulban:
// lásd authVedett.js (a hibaleírás és a működés ott van).
const vedett = (callable) => vedettHivas(auth, callable, frissitsdATokent);

export function signOut() {
  // A helyi Firestore-gyorsítótár KIJELENTKEZÉSKOR MEGMARAD (2026-10-08, tagi kérésre): ugyanaz a tag újra
  // belépve tovább használja. Más felhasználó belépésekor törlődik (lásd gyorsitotarGazda), mielőtt látná.
  kilepesJelol(); // a tag maga lép ki: ez nem munkamenet-elvesztés (belepesEmlekezet.js)
  return firebaseSignOut(auth);
}

/**
 * A saját access/{uid} dokumentum lekérése. A v2 szabályok szerint ez
 * MINDIG engedélyezett a bejelentkezett felhasználónak, inaktív állapot
 * esetén is (lásd firestore.rules, access/{uid} match block).
 * Visszaadja: { active: boolean, role: string } | null (ha még nem
 * létezik a dokumentum, azaz a meghívás még nincs elfogadva/aktiválva).
 */
export async function getMyAccess(uid) {
  try {
    const snap = await getDoc(doc(fs(), "access", uid));
    const adat = snap.exists() ? snap.data() : null;
    if (adat) offline.profilMentes("hozzaferes", adat); // offline hidegindításhoz (v143): a mentés pillanatában érvényes jogosultság
    return adat;
  } catch (err) {
    // Kapcsolat nélkül (és csak bekapcsolt offline-kezelővel) a legutóbb mentett jogosultság szolgál; kapcsolattal a program újra ellenőriz (main.js).
    const mentett = offline.offlineKiszolgalas() ? await offline.profilOffline("hozzaferes").catch(() => null) : null;
    if (mentett) return mentett.adat;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Callable végpontok. A kliens SOHA nem ír közvetlenül a Firestore-ba -
// minden módosítás ezeken a szerveroldalon ellenőrzött végpontokon megy át.
// ---------------------------------------------------------------------------

const callSendMessage = vedett(httpsCallable(functions, "sendMessage"));
const callAdminAction = vedett(httpsCallable(functions, "adminAction"));
const callGetSendStatus = vedett(httpsCallable(functions, "getSendStatus"));
const callSaveProfile = vedett(httpsCallable(functions, "saveProfile"));
const callSuggestNameVariants = vedett(httpsCallable(functions, "suggestNameVariants"));
const callSuggestRoomNames = vedett(httpsCallable(functions, "suggestRoomNames"));
const callRegisterPushToken = vedett(httpsCallable(functions, "registerPushToken"));
const callEditMessage = vedett(httpsCallable(functions, "editMessage"));
const callRetryTranslation = vedett(httpsCallable(functions, "retryTranslation"));
// Fordításjavaslat jóváhagyással (2026-10-19): javaslat, visszavonás, elbírálás - functions/src/translationSuggestion.js.
const callTranslationSuggestion = vedett(httpsCallable(functions, "translationSuggestion"));
// Átirat javítása (2026-10-19): functions/src/transcriptCorrection.js.
const callTranscriptCorrection = vedett(httpsCallable(functions, "transcriptCorrection"));
// Átirat írásmód-modernizálása (v205): a modell hívása percig is tarthat - functions/src/transcriptModernize.js.
const callTranscriptModernize = vedett(httpsCallable(functions, "transcriptModernize", { timeout: 150000 }));
// Fórum (2026-10-09): témakezelés és a tag saját fórum-állapota (functions/src/topics.js, forumAction.js).
const callTopicAction = vedett(httpsCallable(functions, "topicAction"));
const callMoveTopic = vedett(httpsCallable(functions, "moveTopic", { timeout: 540000 }));
const callDeleteTopic = vedett(httpsCallable(functions, "deleteTopic", { timeout: 540000 }));
const callForumAction = vedett(httpsCallable(functions, "forumAction"));
// Téma átalakítása önálló főszobává (admin, 2026-10-15): nagy témánál percekig futhat.
const callConvertTopicToRoom = vedett(httpsCallable(functions, "convertTopicToRoom", { timeout: 540000 }));
// Közös szójegyzék (2026-10-08): get / upsert / delete / history - lásd functions/src/glossary.js.
const callGlossaryAction = vedett(httpsCallable(functions, "glossaryAction"));
const callSendNote = vedett(httpsCallable(functions, "sendNote"));
const callDismissNote = vedett(httpsCallable(functions, "dismissNote"));
const callHideDeletedMarker = vedett(httpsCallable(functions, "hideDeletedMarker"));
const callSaveHomeConfig = vedett(httpsCallable(functions, "saveHomeConfig"));
const callPresence = vedett(httpsCallable(functions, "presence"));
const callReportLandingIssue = vedett(httpsCallable(functions, "reportLandingIssue"));
const callAnnouncementAdmin = vedett(httpsCallable(functions, "announcementAdmin", { timeout: 300000 }));
const callAckAnnouncement = vedett(httpsCallable(functions, "ackAnnouncement"));
const callDeleteMessage = vedett(httpsCallable(functions, "deleteMessage"));
const callMemberAdmin = vedett(httpsCallable(functions, "memberAdmin"));
const callGetMyMembershipCard = vedett(httpsCallable(functions, "getMyMembershipCard"));
const callGetMyNameHistory = vedett(httpsCallable(functions, "getMyNameHistory"));

/**
 * Új üzenet küldése.
 *
 * FONTOS, VALÓS HIBÁBÓL TANULVA: korábban ez a függvény mezőnként sorolta
 * fel a bemenetet, és az `image` mező kimaradt a felsorolásból - a kép
 * feltöltődött a Storage-ba, de SOHA nem került rá az üzenetre, a csak
 * képet tartalmazó üzenet küldése pedig "üres szöveg" hibával elbukott.
 * Ezért a kérés most egy helyen, kifejezetten épül fel, és a kép a
 * `sendMessage` egyetlen opcionális mezője.
 *
 * A szerver elutasít minden ismeretlen mezőt, ezért az `image`-et csak
 * akkor tesszük bele, ha ténylegesen van kép.
 */
export async function sendMessage({
  roomId, clientMessageId, text, sourceLang, noTranslate, image, images, replyToId,
  partIndex, partCount, firstPartId,
  topicId, newTopicTitle,
  file, transcript,
}) {
  const payload = { roomId, clientMessageId, text, sourceLang, noTranslate };
  if (transcript === true) payload.transcript = true; // Átirat (2026-10-19): idegen nyelvű forrásszöveg, nem fordítódik magától
  // Az elhagyható mezőket csak akkor tesszük bele, ha van értékük: a
  // szerver minden ISMERETLEN mezőt elutasít, és egy undefined érték a
  // JSON-ban null-ként jelenne meg.
  if (image) payload.image = image;
  if (Array.isArray(images) && images.length > 0) payload.images = images; // több kép egy bejegyzésben (v211; az image-dzsel együtt nem küldhető)
  if (replyToId) payload.replyToId = replyToId;
  // Hosszú üzenet feldarabolt küldése (2026-09-29) - lásd main.js és
  // functions/src/lib/rateLimit.js. `partIndex` 0 is érvényes érték, ezért
  // a meglétét külön kell nézni, nem elég a "van értéke" (falsy 0) ellenőrzés.
  if (partIndex !== undefined) payload.partIndex = partIndex;
  if (partCount !== undefined) payload.partCount = partCount;
  if (firstPartId) payload.firstPartId = firstPartId;
  // Fórum (2026-10-09): meglévő témába ír (topicId), vagy új témát nyit (newTopicTitle) - a kettő
  // közül legfeljebb az egyik; a válasz új témánál a topicId-t és mindig a sorszámot (postNumber) adja.
  if (topicId) payload.topicId = topicId;
  if (newTopicTitle) payload.newTopicTitle = newTopicTitle;
  // PDF-melléklet (2026-10-10): { path, url, name, size } - egy üzenethez kép VAGY PDF.
  if (file) payload.file = file;
  const res = await callSendMessage(payload);
  return res.data;
}

/**
 * Egy korábbi küldés eredményének lekérdezése.
 *
 * Az újratöltés utáni helyreállítás használja (main.js
 * helyreallitFuggoKuldest, a logika: pendingSend.js): induláskor ebből
 * derül ki, hogy egy félbemaradt küldés megérkezett-e a szerverre.
 */
export async function getSendStatus({ roomId, clientMessageId }) {
  const res = await callGetSendStatus({ roomId, clientMessageId });
  return res.data;
}

/**
 * Egy elküldött üzenet szerkesztése (2026-10-02). Csak a saját, friss
 * üzenet szövege módosítható - a szerver kényszeríti ki az időkeretet és
 * a jogosultságot (functions/src/messageActions.js).
 */
/**
 * Kényszerített újrafordítás egy üzenetre, aminek a fordítása nem sikerült
 * (2026-10-07). A szoba bármely tagja kérheti; a szerver korlátozza (költség).
 */
export async function topicAction(adat) {
  const res = await callTopicAction(adat);
  return res.data;
}

/** Téma áthelyezése másik szobába (admin, 2026-10-19). A hibánál az `err.details.code === "wider-audience"` azt jelzi, hogy megerősítés kell. */
export async function moveTopic({ roomId, topicId, targetRoomId, confirmWider = false }) {
  const res = await callMoveTopic({ roomId, topicId, targetRoomId, confirmWider });
  return res.data;
}

/** Téma végleges törlése a hozzászólásaival és fájljaival (admin, 2026-10-19). */
export async function deleteTopic({ roomId, topicId }) {
  const res = await callDeleteTopic({ roomId, topicId });
  return res.data;
}

/**
 * A szoba hozzászólásai, amelyeken NYITOTT fordításjavaslat vár (2026-10-19, "Elbírálandó javaslatok"): egyszeri lekérdezés, legfeljebb 50.
 * A `hasOpenSuggestions` jelzőt a szerver (translationSuggestion) tartja karban; a tag a szoba tagjaként amúgy is olvashatja a hozzászólásokat.
 */
export async function javaslatosUzenetek(roomId) {
  const snap = await getDocs(query(collection(fs(), "rooms", roomId, "messages"), where("hasOpenSuggestions", "==", true), limit(50)));
  return snap.docs.map((d) => ({ messageId: d.id, ...d.data() }));
}

/** Egy szoba témái (legfeljebb 100, legfrissebb elöl): a téma címe és nyitója az "Elbírálandó javaslatok" soraihoz. */
export async function szobaTemai(roomId) {
  const snap = await getDocs(query(collection(fs(), "rooms", roomId, "topics"), orderBy("lastPostAt", "desc"), limit(100)));
  return snap.docs.map((d) => temaDokumentumbol(d.id, d.data()));
}

/** Egy szoba témái az admin panelhez (egyszeri lekérdezés, legfeljebb 100; admin bármely szoba témáit olvashatja). */
export const ADMIN_TEMA_LAP = 100;
/**
 * Egy szoba témái az admin panelhez, LAPOKBAN (2026-10-19): a `cursor` az előző lap utolsó dokumentuma (az első lapnál null).
 * @returns {Promise<{temak: Array, cursor: object|null, tobb: boolean}>} `tobb`: igaz, ha a lap tele volt (lehet még)
 */
export async function adminTemak(roomId, cursor = null) {
  const alap = [collection(fs(), "rooms", roomId, "topics"), orderBy("lastPostAt", "desc")];
  const snap = await getDocs(cursor ? query(...alap, startAfter(cursor), limit(ADMIN_TEMA_LAP)) : query(...alap, limit(ADMIN_TEMA_LAP)));
  return { temak: snap.docs.map((d) => temaDokumentumbol(d.id, d.data())), cursor: snap.docs.length ? snap.docs[snap.docs.length - 1] : null, tobb: snap.docs.length >= ADMIN_TEMA_LAP };
}

export async function convertTopicToRoom({ roomId, topicId }) {
  const res = await callConvertTopicToRoom({ roomId, topicId });
  return res.data;
}

export async function forumAction(adat) {
  const res = await callForumAction(adat);
  return res.data;
}

export async function glossaryAction(action, payload = {}) {
  const res = await callGlossaryAction({ action, payload });
  return res.data;
}

export async function retryTranslation({ roomId, messageId, requestId, overrideHuman = false }) {
  // overrideHuman: a megerősített újrafordítás felülírhatja az elfogadott emberi javítást (2026-10-19; a régi szöveg az előzménybe kerül).
  const res = await callRetryTranslation({ roomId, messageId, requestId, ...(overrideHuman === true ? { overrideHuman: true } : {}) });
  return res.data;
}

/** Átirat javítása (szerző / admin / az átirat nyelvét beszélő tag): { roomId, messageId, text, note? } */
export async function atiratJavitas({ roomId, messageId, text, note }) {
  const res = await callTranscriptCorrection({ roomId, messageId, text, ...(note ? { note } : {}) });
  return res.data;
}

/** Az átirat modern írásmódra átírása (új AI-hozzászólás lesz belőle): { roomId, messageId } */
export async function atiratModernizalas({ roomId, messageId }) {
  const res = await callTranscriptModernize({ roomId, messageId });
  return res.data;
}

export async function forditasJavaslat(adat) {
  const res = await callTranslationSuggestion(adat);
  return res.data;
}

/** Egy hozzászólás egy nyelvére tett javaslatok (egyszeri olvasás, legfeljebb 50; a szoba tagja és az admin olvashatja), régebbi elöl. */
export async function forditasJavaslatok(roomId, messageId, lang) {
  const snap = await getDocs(query(collection(fs(), "rooms", roomId, "messages", messageId, "suggestions"), where("lang", "==", lang), limit(50)));
  return snap.docs.map((d) => javaslatBol(d.id, d.data())).sort((a, b) => a.at - b.at);
}

/**
 * CETLI (2026-10-07): rövid, privát, eldobható üzenet egy másik tagnak. A szerver ellenőrzi,
 * hogy van-e közös szobátok; a címzett nyelvére lefordítja, ha eltér.
 */
export async function sendNote({ toUid, text, requestId }) {
  const res = await callSendNote({ toUid, text, requestId });
  return res.data;
}

/**
 * A tagok KEZDŐLAPJÁNAK beállítása (2026-10-25, a titkár #86 / #92 javaslata): a kijelölt szobák azonosítói (config/home).
 * Olvasni minden aktív tag olvashatja; hiba / hiányzó dokumentum esetén üres lista (a kezdőlap ilyenkor csak a közleményeket mutatja).
 */
export async function getHomeConfig() {
  try {
    const snap = await getDoc(doc(fs(), "config", "home"));
    const d = snap.exists() ? snap.data() : null;
    const l = d && d.landing && typeof d.landing === "object" && typeof d.landing.roomId === "string" ? d.landing : null;
    return {
      roomIds: d && Array.isArray(d.roomIds) ? d.roomIds.filter((x) => typeof x === "string") : [],
      // A belépési cél (v153, a titkár #94 kérése): a kezdőlap helyett ezt a meglévő szobát / témát nyitjuk 1 óra szünet után.
      landing: l ? { roomId: l.roomId, topicId: typeof l.topicId === "string" && l.topicId ? l.topicId : null } : null,
      // Az ajánlott tájékoztatók (v157, a titkár #96 javaslata): [{ messageId, label, postNumber }], legfeljebb 3.
      pinned: d && Array.isArray(d.pinned) ? d.pinned.filter((p) => p && typeof p.messageId === "string").slice(0, 3).map((p) => ({ messageId: p.messageId, label: typeof p.label === "string" ? p.label : "", customLabel: typeof p.customLabel === "string" ? p.customLabel : "", labels: p.labels && typeof p.labels === "object" ? Object.fromEntries(Object.entries(p.labels).filter(([, v]) => typeof v === "string")) : {}, postNumber: Number.isInteger(p.postNumber) ? p.postNumber : null })) : [],
      // A hibás belépési cél jelzése (v154): ms, vagy null; az adminnak mutatjuk.
      landingIssueAt: d && d.landingIssueAt && typeof d.landingIssueAt.toMillis === "function" ? d.landingIssueAt.toMillis() : null,
    };
  } catch (err) {
    return { roomIds: [], landing: null, pinned: [], landingIssueAt: null };
  }
}

/**
 * SZERVERIDŐ a kezdőlap 1 órás döntéséhez (v154): "ping" (életjel), "logout" (kijelentkezés), "check" (belépéskor: a szerverre bízott döntés:
 * { showHome, awayMs, basis: "logout"|"active"|"none", serverNow }). Eszközönként külön (deviceId).
 */
export async function presence(action, deviceId) {
  const res = await callPresence({ action, deviceId });
  return res.data;
}

/** A hibás belépési cél jelzése az adminnak (a tag belépésekor a cél nem volt megnyitható). Sosem dob a hívónak. */
export async function reportLandingIssue() {
  try {
    const res = await callReportLandingIssue({});
    return res.data;
  } catch (err) {
    return null;
  }
}

/**
 * A belépési cél témájának legutóbbi hozzászólásai az "ajánlott tájékoztatók" kiválasztásához (admin, v157): a nem törölt hozzászólások
 * sorszámmal, rövid részlettel és idővel, legújabb elöl (legfeljebb 30).
 */
export async function landingUzenetek(roomId, topicId, nezoNyelv = null) {
  // A MEGLÉVŐ összetett indexet használjuk (topicId + createdAt csökkenő, lásd firestore.indexes.json): a postNumber szerinti rendezés új indexet
  // kért volna, és hiányában a lekérdezés CSENDBEN elbukott (a titkár #99: "Nincs választható bejegyzés", pedig a témában 16 hozzászólás van).
  // A sorszám szerinti rendezés a kliensen történik; a hibát NEM nyeljük el (a hívó külön mutatja: lekérés sikertelen).
  const snap = await getDocs(query(collection(fs(), "rooms", roomId, "messages"), where("topicId", "==", topicId), orderBy("createdAt", "desc"), limit(50)));
  return snap.docs
    .map((d) => ({ messageId: d.id, ...d.data() }))
    .filter((m) => m.deleted !== true && typeof m.text === "string" && m.text.trim() !== "")
    .map((m) => ({ messageId: m.messageId, postNumber: Number.isInteger(m.postNumber) ? m.postNumber : null, excerpt: kivonatSzoveg(m, nezoNyelv).replace(/\s+/g, " ").trim().slice(0, 120), createdAt: m.createdAt && typeof m.createdAt.toMillis === "function" ? m.createdAt.toMillis() : 0 }))
    .sort((a, b) => (b.postNumber || 0) - (a.postNumber || 0) || b.createdAt - a.createdAt)
    .slice(0, 30);
}

/** A kezdőlap szobáinak mentése (csak admin; a szerver ellenőrzi, hogy a szobák léteznek). */
export async function saveHomeConfig({ roomIds, landing = null, pinned = [] }) {
  const res = await callSaveHomeConfig({ roomIds, landing, pinned });
  return res.data;
}

/**
 * A törölt üzenet "törölve" JELZÉSÉNEK elrejtése / visszaállítása (v151, csak admin; csak a megjelenítést érinti, a szöveg nem tér vissza).
 */
export async function hideDeletedMarker({ roomId, messageId, hidden, requestId }) {
  const res = await callHideDeletedMarker({ roomId, messageId, hidden, requestId });
  return res.data;
}

/** A cetli eldobása = VÉGLEGES törlés (csak a címzett teheti). */
export async function dismissNote({ noteId }) {
  const res = await callDismissNote({ noteId });
  return res.data;
}

/**
 * A SAJÁT cetlik élő figyelése (a felugró ablakhoz). A lekérdezés KÖTELEZŐEN tartalmazza a
 * toUid-szűrőt - a Firestore-szabály ebből engedi (lásd firestore.rules, notes). Rendezés
 * nélkül: egy címzettnél legfeljebb 5 cetli él, a rendezést a kliens végzi (noteData.js),
 * így összetett index sem kell.
 */
export function watchMyNotes(uid, onNotes, onError) {
  const q = query(collection(fs(), "notes"), where("toUid", "==", uid), limit(10));
  // A szűrő magyarázata és az includeMetadataChanges oka: gyorsitotar.js (kezbesitoSzuro).
  const szuro = kezbesitoSzuro((snap) => {
    onNotes(snap.docs.map((d) => cetliDokumentumbol(d.id, d.data())));
  });
  const leiratkozas = onSnapshot(q, { includeMetadataChanges: true }, szuro, onError);
  return () => { szuro.leallit(); leiratkozas(); };
}

/**
 * KÖZLEMÉNY (2026-10-25): admin -> mindenki / kijelölt tagok / egy szoba; a címzett tudomásul veszi, az admin látja, ki vette.
 * action: "send" ({text, audience, uids?, roomId?, days?, requestId}) | "list" | "withdraw" ({annId}).
 */
/** Tagnyilvántartás (v166, csak admin): { action: "list" | "issue" | "setStatus", uid, status, validUntil }. */
export async function memberAdmin(adat) {
  const res = await callMemberAdmin(adat);
  return res.data;
}

/** A bejelentkezett tag SAJÁT tagsági kártyájának adatai: { issued: false } vagy { issued, number, nickname, joinedYear, joinedMonth, status }. */
export async function getMyMembershipCard() {
  const res = await callGetMyMembershipCard({});
  return res.data;
}

// Önkéntes jelentkezés (v190–v191): a jelentkező saját függvényei (nem tag is hívhatja) és a szervezői felület (csak admin). A kliens a gyűjteményhez nem fér.
const callApplicationMine = vedett(httpsCallable(functions, "applicationMine"));
const callApplicationSave = vedett(httpsCallable(functions, "applicationSave"));
const callApplicationSubmit = vedett(httpsCallable(functions, "applicationSubmit"));
const callApplicationWithdraw = vedett(httpsCallable(functions, "applicationWithdraw"));
const callApplicationAdmin = vedett(httpsCallable(functions, "applicationAdmin"));
export async function applicationMine() { return (await callApplicationMine({})).data; }
export async function applicationSave(adat) { return (await callApplicationSave(adat)).data; }
export async function applicationSubmit() { return (await callApplicationSubmit({})).data; }
export async function applicationWithdraw(deleteData) { return (await callApplicationWithdraw({ deleteData: deleteData === true })).data; }
export async function applicationAdmin(adat) { return (await callApplicationAdmin(adat)).data; }

// Karakterszavazó (Paleo, 2026-10-30): get / vote / close; a szavazatokhoz a kliens közvetlenül nem fér.
const callPollVote = vedett(httpsCallable(functions, "pollVote"));
export async function pollVote(adat) { return (await callPollVote(adat)).data; }

/** A bejelentkezett tag SAJÁT naplózott névváltozásai (D-010): { entries: [{ at, changes: { mező: { old, new } } }], enabled }. */
export async function getMyNameHistory() {
  const res = await callGetMyNameHistory({});
  return res.data;
}

export async function announcementAdmin(adat) {
  const res = await callAnnouncementAdmin(adat);
  return res.data;
}

/** A közlemény tudomásul vétele (a saját postaládából törli, az admin nyilvántartásában rögzül). Ismételhető. */
export async function ackAnnouncement({ annId }) {
  const res = await callAckAnnouncement({ annId });
  return res.data;
}

/** A SAJÁT, még tudomásul nem vett közlemények élő figyelése (a toUid-szűrő kötelező: a Firestore-szabály ebből engedi). */
export function watchMyAnnouncements(uid, onList, onError) {
  const q = query(collection(fs(), "announcementInbox"), where("toUid", "==", uid), limit(20));
  const szuro = kezbesitoSzuro((snap) => {
    onList(snap.docs.map((d) => kozlemenyDokumentumbol(d.id, d.data())));
  });
  const leiratkozas = onSnapshot(q, { includeMetadataChanges: true }, szuro, onError);
  return () => { szuro.leallit(); leiratkozas(); };
}

export async function editMessage({ roomId, messageId, text, requestId }) {
  const res = await callEditMessage({ roomId, messageId, text, requestId });
  return res.data;
}

/**
 * Egy elküldött üzenet törlése (2026-10-02). Sírkő marad: a szöveg és a
 * kép eltűnik, de az üzenet helye megmarad ("Az üzenetet törölték").
 */
export async function deleteMessage({ roomId, messageId, requestId }) {
  const res = await callDeleteMessage({ roomId, messageId, requestId });
  return res.data;
}

export async function adminAction(action, payload, requestId) {
  const body = requestId === undefined ? { action, payload } : { action, requestId, payload };
  const res = await callAdminAction(body);
  return res.data;
}

/** Eszköztoken regisztrálása vagy eltávolítása a push értesítésekhez. */
export async function registerPushToken(token, remove = false) {
  const res = await callRegisterPushToken(remove ? { token, remove: true } : { token });
  return res.data;
}

export async function saveProfile({ name, nameLatn, nameJa, nameZh, nameKo, nameCyr, nameEl, preferredLang, lastRoomId }) {
  // FONTOS, VALÓS HIBÁBÓL TANULVA: ez a függvény korábban csak három mezőt
  // (name, preferredLang, lastRoomId) szedett ki a kapott objektumból és
  // pontosan ezt a hármat küldte tovább - a hívó akármit is adott át
  // (nameLatn, nameJa), az itt csendben ELVESZETT, mielőtt a hálózati
  // kérés egyáltalán elindult volna. A profil mentése "sikeresnek"
  // látszott, mert a szerver a kapott (hiányos) adatot rendben elfogadta -
  // a hiba a hálózati kérés PAYLOAD-jában derült ki, nem egy hibaüzenetben.
  // A névváltozatokat (2026-09-30: latin, japán, kínai, koreai, cirill,
  // görög - hat írásrendszer) CSAK akkor tesszük bele, ha a hívó
  // ténylegesen megadta őket. A Firebase SDK az undefined értéket null-lá
  // alakítja, és egy elküldött null a szerver szemszögéből "üresre
  // állítás" lenne - vagyis egy szobaváltás letörölné a mentett
  // névalakokat.
  const payload = { name, preferredLang, lastRoomId };
  if (nameLatn !== undefined && nameLatn !== null) payload.nameLatn = nameLatn;
  if (nameJa !== undefined && nameJa !== null) payload.nameJa = nameJa;
  if (nameZh !== undefined && nameZh !== null) payload.nameZh = nameZh;
  if (nameKo !== undefined && nameKo !== null) payload.nameKo = nameKo;
  if (nameCyr !== undefined && nameCyr !== null) payload.nameCyr = nameCyr;
  if (nameEl !== undefined && nameEl !== null) payload.nameEl = nameEl;
  const res = await callSaveProfile(payload);
  return res.data;
}

/**
 * Javaslat a saját nevünk másik írásmódú alakjára.
 * A választ NEM menti el a szerver - a tag nézi át és menti.
 */
export async function suggestNameVariants(name, nameLang, targets) {
  // `targets` (2026-10-04): a kitöltendő mezők ("nameJa", "nameCyr", ...) - ezzel
  // egyetlen hívás javaslatot ad minden kért írásmódra. Nélküle a régi, latin <->
  // japán viselkedés marad.
  const kerelem = { name };
  if (nameLang) kerelem.nameLang = nameLang;
  if (Array.isArray(targets) && targets.length > 0) kerelem.targets = targets;
  const res = await callSuggestNameVariants(kerelem);
  return res.data;
}

/**
 * Javaslat egy szoba nyelvenkénti neveire (2026-10-04; csak admin/témafelelős).
 * @returns {Promise<Record<string, string>>} nyelvkód -> javasolt név
 */
export async function suggestRoomNames(name, targetLangs, sourceLang, { longText = false, room = false } = {}) {
  const kerelem = { name, targetLangs };
  if (room === true) kerelem.room = true; // szobanév: 160 karakterig (a címkenevek 80-ig)
  if (longText === true) kerelem.longText = true; // útmutató-szoba leírása (500 karakterig)
  if (sourceLang) kerelem.sourceLang = sourceLang;
  const res = await callSuggestRoomNames(kerelem);
  return (res.data && res.data.suggestions) || {};
}

// ---------------------------------------------------------------------------
// Valós idejű figyelők (csak olvasás - ezt a firestore.rules engedélyezi)
// ---------------------------------------------------------------------------

/**
 * A saját szobák élő figyelése.
 *
 * FONTOS: a where('memberIds','array-contains',uid) feltétel KÖTELEZŐ a
 * nem-admin felhasználónál. A Security Rules nem eredményszűrő: feltétel
 * nélkül a TELJES lekérdezés elutasításra kerülne, nem üres listát adna
 * (SPECIFICATION.md 5. szakasz).
 */
export function watchMyRooms(uid, isAdmin, onRooms, onError) {
  const base = collection(fs(), "rooms");
  const q = isAdmin
    ? query(base, orderBy("__name__"), limit(100))
    : query(base, where("memberIds", "array-contains", uid), limit(100));

  let leallitva = false;
  const szuro = kezbesitoSzuro((snap) => {
    // A leképezés külön, tesztelhető modulban van (roomData.js) - benne a parentRoomId is.
    const rooms = snap.docs.map((d) => szobaDokumentumbol(d.id, d.data()));
    if (uresOfflineGyorsitotar(snap, rooms)) return; // offline: a saját tár szolgál (lent)
    if (szerverrolJott(snap)) offline.szobakSzerverrol(rooms);
    onRooms(rooms, forrasJelzes(snap));
  });
  const leiratkozas = onSnapshot(q, { includeMetadataChanges: true }, szuro, onError);
  if (offline.offlineKiszolgalas()) {
    offline.szobakOffline().then((r) => { if (r && !leallitva) onRooms(r.adat, { megerositetlen: true }); }).catch(() => {});
  }
  return () => { leallitva = true; szuro.leallit(); leiratkozas(); };
}

// Egy lapon ennyi üzenet jön (a Firestore-szabályok ennél többet nem is
// engednek egy lekérdezésben - firestore.rules, messages list).
export const UZENET_LAP = 50;

// Az üzenetek Firestore-pillanatképei azonosító szerint: a régebbi lap
// kérésénél ez a pontos kurzor (azonos időbélyegű üzeneteknél is egyértelmű).
//
// FONTOS, AUDITBÓL (2026-09-30): ez a gyorsítótár korábban KORLÁTLANUL nőtt.
// Minden betöltött üzenet pillanatképe (a teljes dokumentum adatával) benne
// maradt - szobaváltásonként és visszalapozásonként újabb 50-nel. Egy napokig
// nyitva hagyott telefonon ez folyamatosan hízó memóriafoglalás volt, amit
// semmi nem szabadított fel.
//
// Most a legutóbb HASZNÁLT néhány száz pillanatképet tartjuk (LRU): a kurzorhoz
// mindig csak a betöltött legrégebbi üzenet kell. Ha egy kiszorult elem mégis
// kellene, a lenti tartalék (createdAt időbélyeg) ugyanúgy jó kurzor - a
// lapozás tehát kiürült gyorsítótárral is helyes marad.
const PILLANATKEP_MAX = 300;
const uzenetPillanatkepek = new Map();
const pillanatkepKulcs = (roomId, messageId) => `${roomId}/${messageId}`;

function pillanatkepTarol(kulcs, doc) {
  // Újra beszúrva a Map végére kerül: a legrégebben érintett elem lesz elöl.
  if (uzenetPillanatkepek.has(kulcs)) uzenetPillanatkepek.delete(kulcs);
  uzenetPillanatkepek.set(kulcs, doc);
  while (uzenetPillanatkepek.size > PILLANATKEP_MAX) {
    uzenetPillanatkepek.delete(uzenetPillanatkepek.keys().next().value);
  }
}

function pillanatkepOlvas(kulcs) {
  const doc = uzenetPillanatkepek.get(kulcs);
  if (doc) pillanatkepTarol(kulcs, doc); // a használat frissíti a sorrendet
  return doc;
}

function uzenetekPillanatkepbol(roomId, docs) {
  return docs.map((d) => {
    pillanatkepTarol(pillanatkepKulcs(roomId, d.id), d);
    return { messageId: d.id, ...d.data() };
  });
}

/** Egy szoba legutóbbi 50 üzenetének élő figyelése. */
/**
 * Fórum (2026-10-09): a szoba témáinak élő listája (legfeljebb 100; a szabály ennél többet nem enged).
 * Sorrend: kitűzöttek, majd a legfrissebb hozzászólás - a végleges sorrendet a forumData.js temaSorrend adja.
 */
export function watchTopics(roomId, onTopics, onError) {
  const q = query(collection(fs(), "rooms", roomId, "topics"), orderBy("pinned", "desc"), orderBy("lastPostAt", "desc"), limit(100));
  let leallitva = false;
  const szuro = kezbesitoSzuro((snap) => {
    const temak = snap.docs.map((d) => temaDokumentumbol(d.id, d.data()));
    if (uresOfflineGyorsitotar(snap, temak)) return; // offline: a saját tár szolgál (lent)
    if (szerverrolJott(snap)) offline.temakSzerverrol(roomId, temak);
    onTopics(temak, forrasJelzes(snap));
  });
  const leiratkozas = onSnapshot(q, { includeMetadataChanges: true }, szuro, onError);
  if (offline.offlineKiszolgalas()) {
    offline.temakOffline(roomId).then((r) => { if (r && !leallitva) onTopics(r.adat, { megerositetlen: true }); }).catch(() => {});
  }
  return () => { leallitva = true; szuro.leallit(); leiratkozas(); };
}

/**
 * A szoba STÁTUSZOS témái a szoba-átfogó munkalistához (2026-10-18): egyszeri lekérdezés (nem élő figyelés), legfeljebb 100 téma.
 * A tag a szoba tagjaként amúgy is olvashatja a szoba témáit (Firestore-szabály); nincs új jogosultság.
 */
export async function statuszosTemak(roomId) {
  const snap = await getDocs(query(
    collection(fs(), "rooms", roomId, "topics"),
    where("workflow", "in", ["new", "ocr", "translating", "review", "done"]),
    limit(100)
  ));
  return snap.docs.map((d) => temaDokumentumbol(d.id, d.data()));
}

/**
 * Fórum (2026-10-09): a témán belüli / előzmény-lekérdezés feltételei (lásd forumData.js temaLekeres).
 *   { tipus: "tema", topicId }  → a téma hozzászólásai;
 *   { tipus: "elozmeny", eddig } → a szoba korábbi (téma nélküli) beszélgetése: az átállás pillanatáig
 *                                   (historyTo) létrejött üzenetek - utána minden üzenet témához tartozik.
 */
function temaFeltetelek(lekeres) {
  if (!lekeres) return [];
  if (lekeres.tipus === "tema") return [where("topicId", "==", lekeres.topicId)];
  if (lekeres.tipus === "elozmeny") {
    return typeof lekeres.eddig === "number" ? [where("createdAt", "<=", Timestamp.fromMillis(lekeres.eddig))] : [where("createdAt", "<", Timestamp.fromMillis(0))];
  }
  return [];
}

export function watchMessages(roomId, onMessages, onError, lekeres = null) {
  const q = query(
    collection(fs(), "rooms", roomId, "messages"),
    ...temaFeltetelek(lekeres),
    orderBy("createdAt", "desc"),
    limit(UZENET_LAP)
  );

  // FONTOS (2026-10-08): tartós gyorsítótárral az SDK az ELSŐ pillanatképet a helyi tárból adja. Az első
  // betöltést a chat "meglévő előzménynek" tekinti (main.js: elsoBetoltes - nem jelez róla, és ebből számolja
  // a "Meddig olvastad" elválasztót), ezért az elavult, gyorsítótárból jövő állapotot online visszatartjuk, és
  // a szerver-visszaigazolást várjuk (időkorláttal; includeMetadataChanges kell hozzá - lásd gyorsitotar.js).
  let leallitva = false;
  const temaAzon = lekeres && lekeres.topicId ? lekeres.topicId : "-";
  const szuro = kezbesitoSzuro((snap) => {
    // Időrendben (régi → új) adjuk vissza, a megjelenítéshez.
    const uzenetek = uzenetekPillanatkepbol(roomId, snap.docs).reverse();
    if (uresOfflineGyorsitotar(snap, uzenetek)) return; // offline: a saját tár szolgál (lent)
    if (szerverrolJott(snap)) offline.uzenetekSzerverrol(roomId, temaAzon, uzenetek);
    onMessages(uzenetek, forrasJelzes(snap));
  });
  const leiratkozas = onSnapshot(q, { includeMetadataChanges: true }, szuro, onError);
  if (offline.offlineKiszolgalas()) {
    offline.uzenetekOffline(roomId, temaAzon).then((r) => { if (r && !leallitva) onMessages(r.adat, { megerositetlen: true }); }).catch(() => {});
  }
  return () => { leallitva = true; szuro.leallit(); leiratkozas(); };
}

/**
 * A megadott üzenetnél RÉGEBBI legfeljebb 50 üzenet (egyszeri lekérés, nem
 * élő figyelés - a régi üzenetek már nem változnak érdemben).
 *
 * @param {string} roomId
 * @param {{messageId: string, createdAt?: object}} legregebbi a már betöltött legrégebbi üzenet
 * @returns {Promise<{messages: object[], hasMore: boolean}>} időrendben (régi → új)
 */
export async function loadOlderMessages(roomId, legregebbi, lekeres = null) {
  const kurzor =
    pillanatkepOlvas(pillanatkepKulcs(roomId, legregebbi.messageId)) ||
    // Tartalék: ha a pillanatkép nincs meg, az időbélyeg is jó kurzor.
    legregebbi.createdAt;
  if (!kurzor) return { messages: [], hasMore: false };
  const snap = await getDocs(
    query(
      collection(fs(), "rooms", roomId, "messages"),
      ...temaFeltetelek(lekeres),
      orderBy("createdAt", "desc"),
      startAfter(kurzor),
      limit(UZENET_LAP)
    )
  );
  return {
    messages: uzenetekPillanatkepbol(roomId, snap.docs).reverse(),
    hasMore: snap.docs.length === UZENET_LAP,
  };
}

/**
 * Egy lap üzenet a KERESÉSHEZ (2026-10-04, keresesView.js / kereso.js): a
 * szoba üzenetei legújabbtól visszafelé, UZENET_LAP (50) darabonként - ennél
 * többet a Firestore-szabály nem enged (firestore.rules, messages/list).
 * A kurzor az előző lap utolsó DOKUMENTUMA, így a lapok pontosan egymás után
 * következnek. A pillanatkép-gyorsítótárat (loadOlderMessages) SZÁNDÉKOSAN
 * nem használja: a keresés több ezer üzenetet olvashat, ami kiszorítaná a
 * chat saját, kis (LRU) gyorsítótárát.
 *
 * @returns {Promise<{uzenetek: object[], kurzor: {id: string, snap: any}|null, vanTobb: boolean}>}
 */
export async function keresesiLap(roomId, kurzor) {
  const alap = [collection(fs(), "rooms", roomId, "messages"), orderBy("createdAt", "desc")];
  const snap = await getDocs(
    kurzor && kurzor.snap
      ? query(...alap, startAfter(kurzor.snap), limit(UZENET_LAP))
      : query(...alap, limit(UZENET_LAP))
  );
  const utolso = snap.docs[snap.docs.length - 1];
  return {
    uzenetek: snap.docs.map((d) => ({ messageId: d.id, ...d.data() })),
    kurzor: utolso ? { id: utolso.id, snap: utolso } : kurzor || null,
    vanTobb: snap.docs.length === UZENET_LAP,
  };
}

/** A saját profil egyszeri lekérése (név, tartalomnyelv). */
export async function getMyProfile(uid) {
  try {
    const snap = await getDoc(doc(fs(), "users", uid));
    const adat = snap.exists() ? snap.data() : null;
    if (adat) offline.profilMentes("profil", adat);
    return adat;
  } catch (err) {
    const mentett = offline.offlineKiszolgalas() ? await offline.profilOffline("profil").catch(() => null) : null;
    if (mentett) return mentett.adat;
    throw err;
  }
}

